import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Star, BookOpen, MapPin, Palette, Users, Sparkles } from 'lucide-react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import { useSEO } from '../hooks/useSEO';
import { trackDemoClick } from '../services/trackingService';
import { PRICING, COPY, fmt } from '../config/pricing';
import SampleItineraryModal, { SampleVoyage } from '../components/SampleItineraryModal';
import { trackSampleView } from '../services/trackingService';

// Honest proof element (doc D4): a fictional-but-labeled demo property brand.
// Fictional-and-labeled = honest; invented named clients = not.
const CASA_DEMO_SAMPLE: SampleVoyage = {
  id: 'casa-demo',
  title: 'Sample — Casa Demo, St. Augustine',
  location: 'St. Augustine, Florida — fictional demo property',
  image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800&q=75&fm=webp&auto=format&fit=crop',
  description:
    'A branded guest dossier exactly as your property would send it — re-skinned in the colors of Casa Demo, a fictional boutique hotel we use so you can judge the real output without invented endorsements.',
  tags: ['Branded Guest Dossier', 'Demo Property'],
  days: [
    {
      day: 1,
      title: 'The Old City Unlocked',
      morning: 'Private walking introduction to the colonial quarter before the crowds — coquina facades, hidden courtyards, café con leche at a family-run roaster.',
      afternoon: 'Curated gallery circuit along Aviles Street, the oldest street in America, with a stop for hand-rolled pastries at a fourth-generation bakery.',
      evening: 'Rooftop aperitivo overlooking the Bridge of Lions, then a chef’s-counter dinner built around the day’s Atlantic catch.',
    },
    {
      day: 2,
      title: 'Salt Air & Slow Hours',
      morning: 'Sunrise beach walk on Anastasia Island — the dunes to yourself, thermos of Spanish hot chocolate from the concierge desk.',
      afternoon: 'Sailing the Matanzas under full canvas; dolphins usually make an appearance where the river meets the inlet.',
      evening: 'Courtyard dinner under string lights and 400-year-old oaks, paired from the cellar’s Iberian list.',
    },
  ],
};

const FEATURES = [
  {
    icon: BookOpen,
    title: 'Bespoke Guest Itineraries',
    body: 'Generate personalized, branded itineraries for arriving guests in seconds. Cover local dining, cultural experiences, day trips, and private excursions — curated to the guest\'s stated preferences.',
  },
  {
    icon: Palette,
    title: 'Property-Branded Design',
    body: 'Every itinerary carries your hotel\'s visual identity — logo, color palette, typography, and voice. Guests experience your brand from the document to the destination.',
  },
  {
    icon: MapPin,
    title: 'Hyper-Local Intelligence',
    body: 'Aura\'s destination engine goes beyond tourist recommendations. Curate the hidden restaurants, private galleries, and local rituals that define genuinely immersive travel — the kind of intel a great concierge carries for years.',
  },
  {
    icon: Star,
    title: 'Elevate the In-Stay Experience',
    body: 'A guest who receives a beautifully designed, thoughtfully curated itinerary at check-in associates that quality with your property. The itinerary extends your brand into the city itself.',
  },
  {
    icon: Users,
    title: 'Concierge Team Enablement',
    body: 'Empower every member of your concierge team to produce itineraries at the quality level of your most seasoned staff. Aura equalizes expertise without eliminating the human layer.',
  },
  {
    icon: Sparkles,
    title: 'Shareable Digital Format',
    body: 'Guests receive their itinerary as a shareable digital link — accessible on any device, offline-ready, and designed for social sharing.',
  },
];

const USE_CASES = [
  {
    title: 'Pre-Arrival Welcome Itineraries',
    body: 'Send guests a personalized itinerary before they arrive. Build anticipation. Establish the tone of their stay before they cross your threshold.',
    image: 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?q=80&w=1200&auto=format&fit=crop',
  },
  {
    title: 'Signature Destination Guides',
    body: 'Produce seasonal, neighborhood-specific guides that reflect the character of your property and its immediate world. Replace generic tourist pamphlets with editorial-quality documents.',
    image: 'https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?q=80&w=1200&auto=format&fit=crop',
  },
  {
    title: 'Curated Day-Trip Experiences',
    body: 'Design half-day and full-day excursions for guests who want more than the lobby. From private vineyard visits to dawn boat departures — logistics included, booking links verified.',
    image: 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?q=80&w=1200&auto=format&fit=crop',
  },
];

const ForHotelsPage: React.FC = () => {
  const [sampleOpen, setSampleOpen] = useState(false);
  useSEO({
    title: 'Aura Traveler for Hotels & Resorts | Elevate the Guest Experience',
    description:
      'AI-powered concierge itinerary platform for luxury hotels and resorts. Generate branded, personalized guest itineraries in seconds. Transform your concierge desk into a world-class design studio.',
    canonical: 'https://www.auratraveler.com/for-hotels',
    breadcrumbs: [
      { name: 'Home', url: 'https://www.auratraveler.com/' },
      { name: 'For Hotels', url: 'https://www.auratraveler.com/for-hotels' },
    ],
  });

  // Set body background to dark to prevent bright flash on load
  useEffect(() => {
    document.body.style.backgroundColor = '#0A0A0A';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  return (
    <div className="min-h-screen bg-charcoal text-white font-sans selection:bg-gold/30 selection:text-white">
      {/* SoftwareApplication schema (doc P0-3), prices from config/pricing.ts */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: `Aura Traveler ${PRICING.atelier.name}`,
        applicationCategory: 'TravelApplication',
        operatingSystem: 'Web',
        url: 'https://www.auratraveler.com/for-hotels',
        description: `Branded guest itineraries for hotels and properties: ${PRICING.atelier.itinerariesPerMonth} dossiers a month with the Concierge Studio and full white-label customization.`,
        offers: { '@type': 'Offer', price: String(PRICING.atelier.priceMonthly), priceCurrency: 'USD' },
      }) }} />
      <SiteNav />

      <main>
      {/* Hero */}
      <section className="pt-40 pb-24 px-4 sm:px-6 md:px-12 lg:px-16 relative overflow-hidden">
        <div
          className="absolute inset-0 z-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage:
              'url("https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?q=80&w=2070&auto=format&fit=crop")',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-charcoal/90 to-charcoal z-0" />
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="max-w-3xl"
          >
            <span className="font-sans text-[10px] uppercase tracking-[0.5em] text-gold/60 mb-8 block">
              For Hoteliers
            </span>
            <h1 className="font-display text-4xl sm:text-6xl md:text-7xl text-white leading-[0.9] tracking-tighter font-bold mb-10">
              Elevate Guest{' '}
              <span className="font-serif italic font-medium text-gold">Status.</span>
            </h1>
            <p className="font-serif text-xl text-white/70 leading-relaxed mb-12 max-w-xl font-medium">
              Transform your concierge desk into a high-fidelity design studio. Provide guests with
              bespoke, branded itineraries that extend the luxury of your property into the surrounding
              world.
            </p>
            <div className="flex flex-col sm:flex-row gap-5">
              <Link
                to="/hotel-concierge"
                className="px-10 py-5 bg-gold text-black font-sans text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white transition-all duration-700 rounded-full text-center"
              >
                Open the Concierge Studio
              </Link>
              <a
                href="https://calendar.app.google/PqvXwzyxy7EwfuNQ9" target="_blank" rel="noopener noreferrer"
                onClick={() => trackDemoClick('for-hotels', 'request_access_hero')}
                className="px-10 py-5 border border-white/20 text-white font-sans text-[10px] font-bold uppercase tracking-[0.3em] hover:border-gold hover:text-gold transition-all duration-700 rounded-full text-center"
              >
                Request Access
              </a>
              <Link
                to="/pricing"
                className="px-10 py-5 border border-white/20 text-white font-sans text-[10px] font-bold uppercase tracking-[0.3em] hover:border-gold hover:text-gold transition-all duration-700 rounded-full text-center"
              >
                View Pricing
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Proposition */}
      <section className="py-24 px-4 sm:px-6 md:px-12 lg:px-16 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
          <div>
            <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/50 mb-8 block">
              The Concierge Revolution
            </span>
            <h2 className="font-display text-3xl md:text-5xl text-white tracking-tighter font-bold mb-8 leading-tight">
              Your concierge, amplified.
            </h2>
            <p className="font-serif text-lg text-white/60 leading-relaxed mb-8 font-medium">
              The best concierge teams in the world carry decades of accumulated local knowledge.
              Aura makes that depth of expertise available to every property and every guest — not
              as a replacement for human expertise, but as an amplifier of it.
            </p>
            <p className="font-serif text-lg text-white/60 leading-relaxed font-medium">
              A guest who receives a beautifully designed, personally curated itinerary at check-in
              associates that quality with your property, not with the tool that produced it. That
              association is permanent, shareable, and measurable in review scores and return
              rates.
            </p>
          </div>
          <div className="space-y-6">
            {[
              { n: '<2min', label: 'Generation Time', sub: 'From guest preferences to a completed, print-ready itinerary' },
              { n: '100%', label: 'Link Verification', sub: 'Every booking link is HTTP-checked and every venue confirmed open against live search before guests see it' },
              { n: `${PRICING.atelier.itinerariesPerMonth}/mo`, label: 'Atelier Itineraries', sub: `Included with the ${fmt(PRICING.atelier.priceMonthly)}/month property plan, with custom volume pricing for groups` },
            ].map(({ n, label, sub }) => (
              <div key={label} className="border border-white/5 bg-[#141414] p-8 hover:border-gold/20 transition-all duration-500">
                <div className="flex items-baseline gap-4 mb-2">
                  <span className="font-display text-4xl text-gold tracking-tighter">{n}</span>
                  <span className="font-sans text-[11px] uppercase tracking-[0.2em] text-white/60">{label}</span>
                </div>
                <p className="font-serif text-sm italic text-white/50">{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Grid */}
      <section className="py-24 px-4 sm:px-6 md:px-12 lg:px-16 bg-charcoal border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/50 mb-6 block">
              Platform Features
            </span>
            <h2 className="font-display text-3xl md:text-6xl text-white tracking-tighter">
              Built for Hospitality
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {FEATURES.map(({ icon: Icon, title, body }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="bg-[#141414] p-10 md:p-12 hover:bg-[#1a1a1a] transition-all duration-500 group"
              >
                <div className="w-12 h-12 border border-white/10 rounded-full flex items-center justify-center mb-8 text-gold/50 group-hover:border-gold group-hover:text-gold transition-all duration-300">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-xl text-white mb-4 tracking-tight group-hover:text-gold transition-colors duration-300">
                  {title}
                </h3>
                <p className="font-serif text-sm text-white/60 leading-relaxed group-hover:text-white/80 transition-colors duration-500">
                  {body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 px-4 sm:px-6 md:px-12 lg:px-16 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="mb-20">
            <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/50 mb-6 block">
              Use Cases
            </span>
            <h2 className="font-display text-3xl md:text-5xl text-white tracking-tighter">
              How Hoteliers Use Aura
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {USE_CASES.map(({ title, body, image }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.15 }}
                className="group"
              >
                <div className="aspect-[4/3] mb-6 overflow-hidden">
                  <img
                    src={image}
                    alt={title}
                    loading="lazy"
                    className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700"
                  />
                </div>
                <h3 className="font-display text-2xl text-white mb-4 tracking-tight group-hover:text-gold transition-colors duration-300">
                  {title}
                </h3>
                <p className="font-serif text-sm text-white/50 leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 sm:px-6 md:px-12 bg-charcoal border-t border-white/5">
        <div className="max-w-2xl mx-auto text-center border border-gold/20 bg-gold/[0.03] p-10 md:p-14">
          <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/60 mb-4 block">{PRICING.atelier.name} — For Properties</span>
          <div className="flex items-baseline justify-center gap-2 mb-4">
            <span className="font-display text-5xl text-gold tracking-tighter">{fmt(PRICING.atelier.priceMonthly)}</span>
            <span className="font-serif text-lg text-white/40 italic">/month</span>
          </div>
          <p className="font-serif text-base text-white/60 leading-relaxed mb-4">
            {PRICING.atelier.itinerariesPerMonth} branded itineraries per month, full white-label customization, the Concierge
            Studio with portfolio venue prioritization, and team access for your whole desk.
          </p>
          <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-gold/60 mb-1">{COPY.atelierAnnualLine}</p>
          <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-gold/60 mb-1">{COPY.foundingPropertyLine}</p>
          <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-gold/60 mb-8">{COPY.hotelGroupsLine}</p>
          {/* Per-guest ROI math (doc D3) */}
          <p className="font-serif text-base text-white/70 italic leading-relaxed mb-8">{COPY.hotelRoi}</p>
          <Link
            to="/hotel-concierge"
            className="inline-block px-12 py-5 bg-gold text-black font-sans text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white transition-all duration-700 rounded-full"
          >
            Start in the Concierge Studio
          </Link>
        </div>
      </section>

      {/* Honest proof (doc D4): a live sample in a fictional, clearly-labeled
          demo property brand — judge the output, no invented endorsements */}
      <section className="py-20 px-4 sm:px-6 md:px-12 text-center bg-charcoal border-t border-white/5">
        <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/60 mb-4 block">See the Real Output</span>
        <h2 className="font-display text-3xl md:text-5xl text-white tracking-tighter mb-6">Judge the dossier, not a testimonial.</h2>
        <p className="font-serif text-base text-white/60 mb-3 max-w-xl mx-auto">
          Below is a full guest dossier re-skinned in the brand of a fictional demo property.
        </p>
        <p className="font-sans text-[10px] uppercase tracking-[0.3em] text-white/40 mb-10">Sample — Casa Demo, St. Augustine · fictional demo brand</p>
        <button
          onClick={() => { trackSampleView('casa-demo-hotels'); setSampleOpen(true); }}
          className="inline-block px-12 py-5 bg-gold text-black font-sans text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white transition-all duration-700 rounded-full"
        >
          View the Sample Dossier
        </button>
      </section>
      <SampleItineraryModal isOpen={sampleOpen} onClose={() => setSampleOpen(false)} voyage={sampleOpen ? CASA_DEMO_SAMPLE : null} />

      {/* CTA */}
      <section className="py-32 px-4 sm:px-6 md:px-12 text-center bg-gradient-to-b from-charcoal to-[#000] border-t border-white/5">
        <h2 className="font-display text-4xl md:text-7xl text-white mb-8 tracking-tighter">
          Transform your{' '}
          <span className="font-serif italic text-gold">concierge desk</span>
        </h2>
        <p className="font-serif text-lg text-white/60 mb-12 max-w-xl mx-auto">
          Contact our team to learn how Aura integrates with your property and your existing guest
          experience workflow.
        </p>
        <a
          href="https://calendar.app.google/PqvXwzyxy7EwfuNQ9" target="_blank" rel="noopener noreferrer"
          onClick={() => trackDemoClick('for-hotels', 'contact_us_footer')}
          className="inline-block px-16 py-6 border border-gold/40 text-gold font-sans text-[10px] font-bold uppercase tracking-[0.4em] hover:bg-gold hover:text-black transition-all duration-700 rounded-full"
        >
          Contact Us
        </a>
      </section>

      </main>

      <SiteFooter />
    </div>
  );
};

export default ForHotelsPage;
